import { useContext } from 'react';
import { AuthContext } from '@/providers/auth-provider';
import { hasPermission, canAccessRoute } from '@/lib/auth/permissions';
import type { Permission, Role } from '@/lib/auth/permissions';

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function usePermissions() {
  const { user } = useAuth();
  const role = user?.role ?? 'user';

  return {
    role,
    hasPermission: (permission: Permission) => hasPermission(role, permission),
    hasAnyPermission: (permissions: Permission[]) => permissions.some((p) => hasPermission(role, p)),
    hasAllPermissions: (permissions: Permission[]) => permissions.every((p) => hasPermission(role, p)),
    canAccessRoute: (requiredRole?: Role) => canAccessRoute(role, requiredRole),
  };
}

export function useIsAuthenticated() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

export function useIsLoading() {
  const { isLoading } = useAuth();
  return isLoading;
}

export function useAuthError() {
  const { error, clearError } = useAuth();
  return { error, clearError };
}
